import {
    normalizeWasteReceiverDoc,
    serializeWasteReceiver,
} from './wasteReceivers.js';

export const WRITE_LEGACY_SORTING_ITEMS = true;

export const SORTING_LIST_MODES = {
    POSITIONAL: 'positional',
    LEGACY: 'legacy',
};

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

const toText = (value) => String(value ?? '');

export function normalizeDanishComparable(value) {
    return toText(value).trim().normalize('NFC').toLocaleLowerCase('da-DK');
}

export function sanitizeLegacySortingItemKey(value) {
    return normalizeDanishComparable(value)
        .replace(/æ/g, 'ae')
        .replace(/ø/g, 'oe')
        .replace(/å/g, 'aa')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
}

export function normalizeTransitionColumns(columns = []) {
    if (!Array.isArray(columns)) return [];

    return columns
        .filter((column) => column && typeof column === 'object')
        .map((column) => {
            const displayName = toText(column.display_name ?? column.displayName).trim();
            const id = toText(column.id).trim();

            return id
                ? { display_name: displayName, id }
                : { display_name: displayName };
        });
}

export function getLegacySortingItemKey(column) {
    const id = toText(column?.id).trim();
    if (id) return id;
    return sanitizeLegacySortingItemKey(column?.display_name);
}

export function validateTransitionColumns(columns = []) {
    const normalizedColumns = normalizeTransitionColumns(columns);
    const blankColumnNumbers = [];
    const invalidLegacyKeyColumnNumbers = [];
    const nameGroups = new Map();
    const legacyKeyGroups = new Map();

    normalizedColumns.forEach((column, index) => {
        const name = column.display_name;
        if (!name) blankColumnNumbers.push(index + 1);

        const comparableName = normalizeDanishComparable(name);
        if (comparableName) {
            const group = nameGroups.get(comparableName) || [];
            group.push({ column, index });
            nameGroups.set(comparableName, group);
        }

        const legacyKey = getLegacySortingItemKey(column);
        if (legacyKey) {
            const group = legacyKeyGroups.get(legacyKey) || [];
            group.push({ column, index });
            legacyKeyGroups.set(legacyKey, group);
        } else if (name) {
            invalidLegacyKeyColumnNumbers.push(index + 1);
        }
    });

    const duplicateNameGroups = Array.from(nameGroups.values()).filter((group) => group.length > 1);
    const legacyKeyCollisionGroups = Array.from(legacyKeyGroups.values())
        .filter((group) => group.length > 1);

    return {
        columns: normalizedColumns,
        blankColumnNumbers,
        invalidLegacyKeyColumnNumbers,
        duplicateNameGroups,
        legacyKeyCollisionGroups,
        isValid:
            blankColumnNumbers.length === 0 &&
            invalidLegacyKeyColumnNumbers.length === 0 &&
            duplicateNameGroups.length === 0 &&
            legacyKeyCollisionGroups.length === 0,
    };
}

function buildColumnLookup(columns = []) {
    const lookup = new Map();
    normalizeTransitionColumns(columns).forEach((column) => {
        const nameKey = normalizeDanishComparable(column.display_name);
        const idKey = normalizeDanishComparable(column.id);
        if (nameKey) lookup.set(nameKey, column);
        if (idKey) lookup.set(idKey, column);
    });
    return lookup;
}

export function resolveColumnReference(reference, columns = []) {
    return buildColumnLookup(columns).get(normalizeDanishComparable(reference)) || null;
}

export function normalizeColumnReferencesToNames(references = [], columns = []) {
    if (!Array.isArray(references)) return [];

    const lookup = buildColumnLookup(columns);
    const seen = new Set();
    return references.reduce((names, reference) => {
        const column = lookup.get(normalizeDanishComparable(reference));
        const name = column?.display_name;
        const nameKey = normalizeDanishComparable(name);
        if (!name || seen.has(nameKey)) return names;
        seen.add(nameKey);
        names.push(name);
        return names;
    }, []);
}

export function getVisibleTransitionColumns(columns = [], hiddenReferences = []) {
    const hiddenNames = new Set(
        normalizeColumnReferencesToNames(hiddenReferences, columns).map(normalizeDanishComparable),
    );
    return normalizeTransitionColumns(columns).filter(
        (column) => !hiddenNames.has(normalizeDanishComparable(column.display_name)),
    );
}

export function replaceColumnReference(references = [], previousColumn, nextName, columns = []) {
    if (!Array.isArray(references)) return [];
    const previousReferences = new Set([
        normalizeDanishComparable(previousColumn?.id),
        normalizeDanishComparable(previousColumn?.display_name),
    ].filter(Boolean));

    const renamedReferences = references.map((reference) => (
        previousReferences.has(normalizeDanishComparable(reference)) ? nextName : reference
    ));

    const nextColumns = normalizeTransitionColumns(columns).map((column) => (
        previousReferences.has(normalizeDanishComparable(column.display_name))
            ? { ...column, display_name: nextName }
            : column
    ));
    return normalizeColumnReferencesToNames(renamedReferences, nextColumns);
}

export function normalizePositionalSortingList(list = [], columns = []) {
    const width = normalizeTransitionColumns(columns).length;
    if (!Array.isArray(list)) return [];

    return list.map((row) => {
        const sourceValues = Array.isArray(row?.values)
            ? row.values
            : Array.isArray(row)
                ? row
                : [];
        return {
            values: Array.from({ length: width }, (_, index) => toText(sourceValues[index])),
        };
    });
}

export function createSortingTableColumns(columns = [], widthByLegacyKey = {}) {
    return normalizeTransitionColumns(columns).map((column, index) => {
        const legacyKey = getLegacySortingItemKey(column);
        return {
            ...column,
            field: `column_${index}`,
            label: column.display_name,
            legacyKey,
            width: widthByLegacyKey[legacyKey] || 'minmax(160px, 1fr)',
            columnIndex: index,
        };
    });
}

const rowId = (prefix, index) => `${prefix}-${index}`;

export function positionalRowsToTableRows(list = [], columns = []) {
    const tableColumns = createSortingTableColumns(columns);
    return normalizePositionalSortingList(list, columns).map((row, rowIndex) => {
        const tableRow = { id: rowId('positional', rowIndex) };
        tableColumns.forEach((column, columnIndex) => {
            tableRow[column.field] = row.values[columnIndex];
        });
        return tableRow;
    });
}

// LEGACY_SORTING_ITEMS: Reads deprecated keyed rows through one compatibility adapter.
export function legacyRowsToTableRows(sortingItems = [], columns = []) {
    const tableColumns = createSortingTableColumns(columns);
    if (!Array.isArray(sortingItems)) return [];

    return sortingItems.map((item, rowIndex) => {
        const tableRow = { id: rowId('legacy', rowIndex) };
        tableColumns.forEach((column) => {
            tableRow[column.field] = toText(
                item?.[column.legacyKey] ?? item?.[column.display_name],
            );
        });
        return tableRow;
    });
}

// LEGACY_SORTING_ITEMS: Normalizes legacy table drafts without leaking keyed-row rules to pages.
export function normalizeLegacyTableRows(rows = [], columns = [], { keepBlank = false } = {}) {
    const tableColumns = createSortingTableColumns(columns);
    if (!Array.isArray(rows)) return [];

    return rows
        .map((row, index) => {
            const normalizedRow = {
                id: toText(row?.id).trim() || rowId('legacy-draft', index),
            };
            tableColumns.forEach((column) => {
                normalizedRow[column.field] = toText(row?.[column.field]);
            });
            return normalizedRow;
        })
        .filter((row) => keepBlank || tableColumns.some(
            (column) => toText(row[column.field]).trim(),
        ));
}

// LEGACY_SORTING_ITEMS: Reports how many deprecated keyed rows still resolve against the
// receiver's current columns. A row keyed by neither a column id nor a column name (a column
// id changed after the rows were stored, say) renders blank and is dropped by
// normalizeLegacyTableRows, so callers must say so rather than show an empty list — and must
// never write the surviving rows back over the stored ones.
export function summarizeLegacyRowMapping(sortingItems = [], columns = []) {
    const rows = Array.isArray(sortingItems) ? sortingItems : [];
    const tableColumns = createSortingTableColumns(columns);
    const columnKeys = new Set();
    tableColumns.forEach((column) => {
        if (column.legacyKey) columnKeys.add(column.legacyKey);
        if (column.display_name) columnKeys.add(column.display_name);
    });

    const unmatchedKeys = [];
    const seenKeys = new Set();
    rows.forEach((row) => {
        if (!row || typeof row !== 'object') return;
        Object.keys(row).forEach((key) => {
            if (columnKeys.has(key) || seenKeys.has(key)) return;
            seenKeys.add(key);
            unmatchedKeys.push(key);
        });
    });

    // A row counts as mapped when at least one of its keys names a current column. Rows that
    // are merely blank in every column stay mapped, so the established blank-row pruning is
    // unchanged; only rows the current columns cannot address at all are reported unmapped.
    const mapped = rows.filter((row) => (
        row && typeof row === 'object' && Object.keys(row).some((key) => columnKeys.has(key))
    )).length;

    return {
        total: rows.length,
        mapped,
        unmapped: rows.length - mapped,
        unmatchedKeys,
    };
}

export function tableRowsToPositionalList(rows = [], columns = []) {
    const tableColumns = createSortingTableColumns(columns);
    if (!Array.isArray(rows)) return [];

    return rows.map((row) => ({
        values: tableColumns.map((column) => toText(row?.[column.field])),
    }));
}

// LEGACY_SORTING_ITEMS: Writes deprecated keyed rows through one compatibility adapter.
export function tableRowsToLegacySortingItems(rows = [], columns = []) {
    const tableColumns = createSortingTableColumns(columns);
    if (!Array.isArray(rows)) return [];

    return rows.map((row) => tableColumns.reduce((item, column) => {
        item[column.legacyKey] = toText(row?.[column.field]);
        return item;
    }, {}));
}

export function validatePositionalTableRows(rows = [], columns = []) {
    const positionalRows = tableRowsToPositionalList(rows, columns);
    const blankFirstValueRows = [];
    const rowNumbersByKey = new Map();

    positionalRows.forEach((row, index) => {
        const key = normalizeDanishComparable(row.values[0]);
        if (!key) {
            blankFirstValueRows.push(index + 1);
            return;
        }
        const rowNumbers = rowNumbersByKey.get(key) || [];
        rowNumbers.push(index + 1);
        rowNumbersByKey.set(key, rowNumbers);
    });

    const duplicateFirstValueRowGroups = Array.from(rowNumbersByKey.values())
        .filter((rowNumbers) => rowNumbers.length > 1);

    return {
        rows: positionalRows,
        blankFirstValueRows,
        duplicateFirstValueRowGroups,
        isValid: blankFirstValueRows.length === 0 && duplicateFirstValueRowGroups.length === 0,
    };
}

function normalizeTransitionSortingList(rawSortingList = {}, fallbackSortingList = {}, columns = []) {
    const raw = rawSortingList && typeof rawSortingList === 'object' ? rawSortingList : {};
    const fallback = fallbackSortingList && typeof fallbackSortingList === 'object'
        ? fallbackSortingList
        : {};
    const hasPositionalList = hasOwn(raw, 'list');
    const hasLegacySortingItems = hasOwn(raw, 'sorting_items');

    // LEGACY_SORTING_ITEMS: Preserve the stored legacy rows exactly until deliberate removal.
    const sortingItems = hasOwn(raw, 'sorting_items')
        ? raw.sorting_items
        : fallback.sortingItems;

    return {
        list: normalizePositionalSortingList(
            hasOwn(raw, 'list') ? raw.list : fallback.list,
            columns,
        ),
        sortingItems: Array.isArray(sortingItems) ? sortingItems : [],
        version: raw.version ?? fallback.version ?? 0,
        lastUpdate: raw.last_update ?? fallback.lastUpdate ?? null,
        hasPositionalList,
        hasLegacySortingItems,
    };
}

export function normalizeTransitionWasteReceiverDoc(snapshot) {
    const base = normalizeWasteReceiverDoc(snapshot);
    const data = snapshot.data() || {};
    const columns = normalizeTransitionColumns(
        Array.isArray(data.columns) ? data.columns : base.columns,
    );
    const sortingList = normalizeTransitionSortingList(
        data.sorting_list,
        base.sortingList,
        columns,
    );

    return {
        ...base,
        columns,
        noShowColumns: normalizeColumnReferencesToNames(
            data.no_show_columns ?? base.noShowColumns,
            columns,
        ),
        groupingColumns: normalizeColumnReferencesToNames(
            data.grouping_columns ?? base.groupingColumns,
            columns,
        ),
        readAloudColumns: normalizeColumnReferencesToNames(
            data.read_aloud_columns ?? base.readAloudColumns,
            columns,
        ),
        sortingList,
    };
}

export function selectSortingListMode(sortingList = {}) {
    // LEGACY_SORTING_ITEMS: Existing populated legacy lists retain their current experience.
    return Array.isArray(sortingList.sortingItems) && sortingList.sortingItems.length > 0
        ? SORTING_LIST_MODES.LEGACY
        : SORTING_LIST_MODES.POSITIONAL;
}

function serializeTransitionSortingList(sortingList = {}, columns = []) {
    const serialized = {
        version: sortingList.version ?? 0,
        last_update: sortingList.lastUpdate ?? null,
    };

    if (sortingList.hasPositionalList === true) {
        serialized.list = normalizePositionalSortingList(sortingList.list, columns);
    }

    // LEGACY_SORTING_ITEMS: The flag stops legacy writes but intentionally never deletes data.
    if (
        WRITE_LEGACY_SORTING_ITEMS &&
        sortingList.hasLegacySortingItems === true
    ) {
        serialized.sorting_items = Array.isArray(sortingList.sortingItems)
            ? sortingList.sortingItems
            : [];
    }

    return serialized;
}

export function serializeTransitionWasteReceiver(receiver = {}) {
    const columns = normalizeTransitionColumns(receiver.columns);
    const payload = serializeWasteReceiver({ ...receiver, columns });

    payload.columns = columns;
    if (hasOwn(receiver, 'noShowColumns')) {
        payload.no_show_columns = normalizeColumnReferencesToNames(receiver.noShowColumns, columns);
    }
    if (hasOwn(receiver, 'groupingColumns')) {
        payload.grouping_columns = normalizeColumnReferencesToNames(receiver.groupingColumns, columns);
    }
    if (hasOwn(receiver, 'readAloudColumns')) {
        payload.read_aloud_columns = normalizeColumnReferencesToNames(receiver.readAloudColumns, columns);
    }
    if (receiver.sortingList) {
        payload.sorting_list = serializeTransitionSortingList(receiver.sortingList, columns);
    }
    return payload;
}

export function buildSortingListWritePayload({
    existingSortingList = {},
    mode,
    rows,
    columns,
    version,
    lastUpdate,
}) {
    const nextSortingList = {
        ...existingSortingList,
        version,
        lastUpdate,
    };

    if (mode === SORTING_LIST_MODES.POSITIONAL) {
        nextSortingList.list = tableRowsToPositionalList(rows, columns);
        nextSortingList.hasPositionalList = true;
    } else {
        // LEGACY_SORTING_ITEMS: Only the legacy mode may change deprecated keyed rows.
        nextSortingList.sortingItems = tableRowsToLegacySortingItems(rows, columns);
        nextSortingList.hasLegacySortingItems = true;
    }

    return {
        sorting_list: serializeTransitionSortingList(nextSortingList, columns),
    };
}
